'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { Cms } from '@bytel/product-wall';
import { Alignable, Container, Tab, TabList, Tabs as TrilogyTabs } from '@bytel/trilogy-react';

import { publicationsService } from '@services/publications';

import { NumeriquesReviewInformation } from '@app-types/numeriques';
import { ProductChildFull, ProductParentFull, ProductType } from '@app-types/product';

import { Characteristics, Faq, Review, Video } from '@product-detail/components';
import { getProductFAQ, sendProductTabsTracking } from '@product-detail/helpers';

import { Partner } from './partner';

export enum TabsEnum {
    HIGHLIGHTS = 'Points forts',
    VIDEO_PRESENTATION = 'Présentation vidéo',
    CHARACTERISTICS = 'Caractéristiques',
    PARTNER = 'Les Numériques',
    REVIEWS = 'Avis clients',
    FAQ = 'FAQ',
}

export type TabsProps = {
    product: ProductParentFull;
    currentProduct: ProductChildFull;
    activeTab: TabsEnum;
    setActiveTab: (tab: TabsEnum) => void;
    isRenewal: boolean;
};

export function Tabs({ product, currentProduct, activeTab, setActiveTab, isRenewal }: TabsProps) {
    const [partnerData, setPartnerData] = useState<NumeriquesReviewInformation['publications'][number]>();
    const { gencode, name, highlightsCms, reviews, videoUrl, type, title, partnerId } = product;
    const faq = getProductFAQ(product, currentProduct);

    const tabConfig: Record<TabsEnum, boolean> = useMemo(
        () => ({
            [TabsEnum.HIGHLIGHTS]: highlightsCms !== undefined,
            [TabsEnum.VIDEO_PRESENTATION]: videoUrl !== undefined,
            [TabsEnum.CHARACTERISTICS]: type === ProductType.PHONE,
            [TabsEnum.PARTNER]: partnerId !== undefined,
            [TabsEnum.REVIEWS]: reviews !== undefined,
            [TabsEnum.FAQ]: faq.length > 1,
        }),
        [product],
    );

    const renderTabContent = useMemo(() => {
        switch (activeTab) {
            case TabsEnum.HIGHLIGHTS:
                return <Cms content={highlightsCms} id='highlights-section' />;
            case TabsEnum.VIDEO_PRESENTATION:
                return <Video name={name} videoUrl={videoUrl as string} />;
            case TabsEnum.CHARACTERISTICS:
                return <Characteristics product={currentProduct} />;
            case TabsEnum.PARTNER:
                return <Partner partnerData={partnerData} />;
            case TabsEnum.REVIEWS:
                return <Review title={title} product={gencode} reviews={reviews} />;
            case TabsEnum.FAQ:
                return <Faq product={product} faq={faq} />;
            default:
                return null;
        }
    }, [activeTab]);

    useEffect(() => {
        sendProductTabsTracking(isRenewal, activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (partnerId) {
            publicationsService.getProductsTestsPublications([partnerId]).then((data) => {
                setPartnerData(data[0]);
            });
        }
    }, [partnerId]);

    if (Object.values(tabConfig).every((isVisible) => !isVisible)) return null;

    return (
        <Container>
            <TrilogyTabs small>
                <TabList align={Alignable.ALIGNED_START}>
                    {Object.entries(tabConfig).map(
                        ([tabKey, isVisible]) =>
                            isVisible && (
                                <Tab
                                    label={tabKey}
                                    onClick={() => setActiveTab(tabKey as TabsEnum)}
                                    active={activeTab === tabKey}
                                    key={tabKey}
                                />
                            ),
                    )}
                </TabList>
            </TrilogyTabs>
            {renderTabContent}
        </Container>
    );
}
